import AppStoreContext from 'contexts/AppStoreContext';
import ECSMapContext from 'contexts/ECSMapContext';
import ECSWorldContext from 'contexts/ECSWorldContext';
import MapboxCamera from 'editor/components/map/MapboxCamera';
import MapboxTool, { MapboxTools } from 'editor/components/map/MapboxTool';
import Markup from 'editor/components/Markup';
import RFIPost from 'editor/components/RFIPost';
import noop from 'lodash/noop';
import React, { FC, useContext, useEffect } from 'react';
import { PostRFI } from 'records/interactions/PostRFI';
import { MappedSheetVersion } from 'records/MappedSheetVersion';

// NOTE: I Think the occurence of finding an Interaction, and then passing it to
//       a child component will become super common, and it would be nice to find
//       a more reusable way of doing this in the future.

type ManagePostRFIInteractionProps = {
  interaction: PostRFI;
};

const ManagePostRFIInteraction: FC<ManagePostRFIInteractionProps> = (props) => {
  const { interaction } = props;
  const { map: mapComp } = useContext(ECSMapContext);
  const world = useContext(ECSWorldContext);
  const { state: appState } = useContext(AppStoreContext);

  useEffect(() => {
    const { rfi, sheetid, pageid, updatingMarkup, markup } = interaction;

    const mappedSheetVersion = appState.mappedSheetVersions.get(
      pageid,
      new MappedSheetVersion()
    );

    const sheetData = mappedSheetVersion.imagesource?.calibrationconfig;
    if (!sheetData) return noop;
    if (!mapComp.map) return noop;

    const { bearing } = sheetData;

    const { bottomright, topleft } = sheetData.corners;

    const topLeftPixels = mapComp.map.project([
      topleft.longitude,
      topleft.latitude,
    ]);

    const bottomRightPixels = mapComp.map.project([
      bottomright.longitude,
      bottomright.latitude,
    ]);

    const mapboxCameraComp = world.grab(MapboxCamera);

    if (mapboxCameraComp) {
      mapboxCameraComp.component.tracked.screenCoordinates = {
        topLeft: topLeftPixels,
        bottomRight: bottomRightPixels,
        bearing: -bearing,
      };
    }

    // If UpdatingMarkup is false, then user is creating a new RFI Post.
    if (!updatingMarkup) {
      world.grabAll(Markup).forEach(({ component }) => {
        component.hidden = true;
        component.selected = false;
      });
    } else if (markup !== null) {
      // Markup is present, probably updating stuff.
      world.grabAll(Markup).forEach(({ component }) => {
        if (component.markup.markupid !== markup?.markupid) {
          component.hidden = true;
        } else {
          component.selected = true;
        }
      });
    }

    const entity = world.createEntity().add(new RFIPost(rfi, sheetid, pageid));

    return () => {
      world.grabAll(Markup).forEach(({ component }) => {
        // Unhide all hidden markups when not in Post RFI "mode"
        component.hidden = false;
        // Temp deselect of all markups after RFI Post stuff.
        component.selected = false;
      });

      world.grabAll(MapboxTool).forEach(({ component }) => {
        component.tool = MapboxTools.Pointer;
      });

      const _mapboxCameraComp = world.grab(MapboxCamera);
      if (_mapboxCameraComp) {
        _mapboxCameraComp.component.tracked.locked = false;
      }

      entity.destroy();
    };
  }, []);

  useEffect(() => {
    const { markup } = interaction;

    if (markup === null) {
      world.grabAll(MapboxTool).forEach(({ component }) => {
        component.tool = MapboxTools.PostRFI;
      });
    }
  }, [interaction]);

  return null;
};

const ManagePostRFIInteractionWrapper: FC = () => {
  const { state: appState } = useContext(AppStoreContext);
  const a = appState.interactions.filter(
    (i): i is PostRFI => i.type === 'PostRFI'
  );

  if (a.size === 1) {
    return <ManagePostRFIInteraction interaction={a.get(0, new PostRFI())} />;
  }

  return null;
};

export default ManagePostRFIInteractionWrapper;
